// src/components/SlashMenu.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { buildToolItems } from './tiptap/toolItems';

const {
  FiType, FiList, FiCheckSquare, FiMessageSquare, FiMinus, FiHash,
  FiPlus, FiCode, FiBold, FiItalic, FiUnderline, FiMic, FiTarget,
  FiChevronRight, FiChevronLeft, FiTool, FiHeadphones, FiGrid, FiPaperclip
} = FiIcons;

export default function SlashMenu({ editor, range, onClose, menuPosition }) {
  const [view, setView] = useState('root'); // 'root' | 'tools'
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef(null);
  const itemsRef = useRef([]);

  // ───────────────────────────────────────────────────────────
  // Commands
  const rootCommands = useMemo(() => ([
    {
      title: 'Attachment',
      description: 'Upload a file or image inline',
      icon: FiPaperclip,
      command: () => {
        if (!editor) return;
        if (!editor.schema.nodes['attachment']) {
          console.error('[SlashMenu] attachment node not registered.');
          return;
        }
        const lid = editor?.storage?.mms?.lessonNoteId ?? null;
        const nbid = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : String(Date.now());
        const chain = editor.chain().focus();
        if (range) chain.deleteRange(range);
        chain.insertContent([{ type: 'attachment', attrs: { lessonNoteId: lid, noteBlockId: nbid } }, { type: 'paragraph' }]).run();
      },
    },
    {
      title: 'Add tool',
      description: 'Metronome, Audio Recorder, Practice Tracker…',
      icon: FiTool,
      isNav: true,
      command: () => setView('tools'),
    },
    {
      title: 'Paragraph',
      description: 'Just start writing with plain text',
      icon: FiType,
      command: () => editor?.chain().focus().deleteRange(range).setParagraph().run(),
    },
    {
      title: 'Heading 1',
      description: 'Big section heading',
      icon: FiHash,
      command: () => editor?.chain().focus().deleteRange(range).setHeading({ level: 1 }).run(),
    },
    {
      title: 'Heading 2',
      description: 'Medium section heading',
      icon: FiHash,
      command: () => editor?.chain().focus().deleteRange(range).setHeading({ level: 2 }).run(),
    },
    {
      title: 'Bullet List',
      description: 'Create a simple bullet list',
      icon: FiList,
      command: () => editor?.chain().focus().deleteRange(range).toggleBulletList().run(),
    },
    {
      title: 'Numbered List',
      description: 'Create a list with numbering',
      icon: FiList,
      command: () => editor?.chain().focus().deleteRange(range).toggleOrderedList().run(),
    },
    {
      title: 'To-do List',
      description: 'Track tasks with a to-do list',
      icon: FiCheckSquare,
      command: () => editor?.chain().focus().deleteRange(range).toggleTaskList().run(),
    },
    {
      title: 'Quote',
      description: 'Capture a quote',
      icon: FiMessageSquare,
      command: () => editor?.chain().focus().deleteRange(range).setBlockquote().run(),
    },
    {
      title: 'Divider',
      description: 'Visually divide blocks',
      icon: FiMinus,
      command: () => editor?.chain().focus().deleteRange(range).setHorizontalRule().run(),
    },
    {
      title: 'Code Block',
      description: 'Display formatted code',
      icon: FiCode,
      command: () => editor?.chain().focus().deleteRange(range).toggleCodeBlock().run(),
    },
    {
      title: 'Bold',
      description: 'Make text bold',
      icon: FiBold,
      command: () => editor?.chain().focus().deleteRange(range).toggleBold().run(),
    },
    {
      title: 'Italic',
      description: 'Make text italic',
      icon: FiItalic,
      command: () => editor?.chain().focus().deleteRange(range).toggleItalic().run(),
    },
    {
      title: 'Underline',
      description: 'Underline text',
      icon: FiUnderline,
      command: () => editor?.chain().focus().deleteRange(range).toggleUnderline().run(),
    },
  ]), [editor, range]);

  const toolItems = useMemo(() => {
    // Build the same items, but ensure we delete the "/" range before inserting
    return buildToolItems(editor, { deleteRange: true, range });
  }, [editor, range]);

  const commands = view === 'root' ? rootCommands : toolItems;

  // ───────────────────────────────────────────────────────────
  // Refs & selection
  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, commands.length);
    setSelectedIndex(0);
  }, [commands.length, view]);

  const executeSelectedCommand = () => {
    const item = commands[selectedIndex];
    if (!item) return;
    item.command();
    if (!item.isNav) onClose?.();
  };

  // ───────────────────────────────────────────────────────────
  // Keyboard
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    el.focus();

    const onKey = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % commands.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + commands.length) % commands.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        executeSelectedCommand();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (view === 'tools') setView('root');
        else onClose?.();
      } else if (e.key === 'ArrowLeft' && view === 'tools') {
        e.preventDefault();
        setView('root');
      }
    };

    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [commands.length, selectedIndex, onClose, view]);

  useEffect(() => {
    const el = itemsRef.current[selectedIndex];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // ───────────────────────────────────────────────────────────
  // Placement
  const shouldPositionAbove = menuPosition && menuPosition.bottom > window.innerHeight - 250;
  const getPositionStyles = () => {
    if (!menuPosition) return {};
    return shouldPositionAbove
      ? { bottom: `calc(100% - ${menuPosition.top}px)`, left: `${menuPosition.left}px`, position: 'fixed' }
      : { top: `${menuPosition.top}px`, left: `${menuPosition.left}px`, position: 'fixed' };
  };

  // ───────────────────────────────────────────────────────────
  // Row handlers: execute on pointer down (early), stop propagation, keep focus
  const handleRowPointerDown = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    item.command();
    if (!item.isNav) onClose?.();
    else setView('tools');
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        tabIndex={0}
        role="menu"
        aria-label="Insert menu"
        initial={{ opacity: 0, y: shouldPositionAbove ? 10 : -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className="absolute z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-64 max-h-96 overflow-y-auto outline-none"
        style={getPositionStyles()}
        // This keeps the editor from stealing focus on mouse down without killing row events
        onMouseDown={(e) => e.preventDefault()}
      >
        {/* Back header for tools view */}
        {view === 'tools' && (
          <div
            role="button"
            tabIndex={0}
            className="w-full px-3 py-2 text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 cursor-pointer"
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); setView('root'); }}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); setView('root'); }}
          >
            <SafeIcon icon={FiChevronLeft} className="text-gray-500" />
            <span className="font-medium">{'< Tools'}</span>
          </div>
        )}

        {/* Items */}
        {commands.map((item, index) => {
          const isActive = index === selectedIndex;
          return (
            <div
              key={item.title}
              ref={(el) => { itemsRef.current[index] = el; }}
              role="menuitem"
              tabIndex={-1}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 ${
                isActive ? 'bg-gray-100' : ''
              } cursor-pointer`}
              onPointerDown={(e) => handleRowPointerDown(e, item)}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onTouchStart={(e) => handleRowPointerDown(e, item)}
            >
              <SafeIcon icon={item.icon} className="text-gray-400 text-base shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-gray-900 text-sm truncate">{item.title}</div>
                <div className="text-xs text-gray-500 truncate">{item.description}</div>
              </div>
              {item.isNav && <FiChevronRight className="text-gray-400 shrink-0" />}
            </div>
          );
        })}
      </motion.div>
    </AnimatePresence>
  );
}